import { Archive, CheckCircle2, Megaphone, Pause, Pencil, Play, Plus, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiRequest, ApiError } from '../../api/http';
import { Link } from '../../app/router';
import { useAuth } from '../../auth/AuthContext';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';
import { ErrorState, Modal, Spinner } from '../../shared/components';
import './merchant-campaigns.css';

// Merchant campaign list, matching the app: current / archived tabs, place
// scope, and edit · pause/resume · archive per campaign. System lots never show.

type Row = Record<string, unknown>;
const num = (value: unknown) => Number(value || 0);
const pts = (value: unknown) => `${num(value).toLocaleString('th-TH')} pts`;
const day = (value: unknown) => { const text = String(value || ''); if (!text) return ''; const d = new Date(text); return Number.isNaN(d.getTime()) ? text.slice(0, 10) : d.toLocaleDateString('th-TH', { dateStyle: 'medium' }); };
export function isMerchantCampaign(item: Row) {
  return !['source', 'funding_source', 'budget_owner', 'campaign_type'].some(key => /^(system|admin)/.test(String(item[key] || '').toLowerCase()));
}
const isArchived = (item: Row) => { const status = String(item.status || '').trim().toLowerCase(); return status.includes('archive') || status === 'deleted' || status === 'removed'; };
function statusLabel(status: string) {
  switch (status.trim().toLowerCase()) {
    case 'pending': case 'pending_approval': return 'รออนุมัติ';
    case 'approved': case 'active': return 'เปิดใช้งาน';
    case 'paused': return 'หยุดชั่วคราว';
    case 'rejected': return 'ถูกปฏิเสธ';
    case 'closed': case 'completed': return 'จบแคมเปญ';
    case 'draft': return 'ฉบับร่าง';
    case '': return '-';
    default: return status.replaceAll('_', ' ');
  }
}
const ACTION_DONE: Record<string, string> = { pause: 'หยุดแคมเปญชั่วคราวแล้ว', resume: 'เปิดใช้งานแคมเปญแล้ว', delete: 'Archive แคมเปญแล้ว' };

function Progress({ label, current, total }: { label: string; current: number; total: number }) {
  const ratio = total > 0 ? Math.min(1, current / total) : 0;
  return <div className="mcl-progress"><span><b>{label}</b><small>{current.toLocaleString('th-TH')} / {total > 0 ? total.toLocaleString('th-TH') : '—'}</small></span><i><em style={{ width: `${ratio * 100}%` }} /></i></div>;
}

function CampaignCard({ item, busy, onAction }: { item: Row; busy: boolean; onAction: (item: Row, action: 'pause' | 'resume' | 'delete') => void }) {
  const id = String(item.campaign_id || item._id || '');
  const status = String(item.status || '').toLowerCase();
  const archived = isArchived(item);
  const slots = num(item.review_slots || item.target_review_count || item.rights_total) || num(item.rights_claimed) + num(item.rights_remaining);
  const people = num(item.max_reviewer_count) || slots;
  const banner = String(item.banner_image || item.banner_url || ((item.banner_images as string[]) || [])[0] || '');
  const place = String(item.place_name || item.cafe_name || '');
  const conditions = [
    place && `พื้นที่: ${place}`,
    num(item.minimum_photo_count) > 0 && `รูปรีวิวอย่างน้อย ${num(item.minimum_photo_count)} รูป${item.photo_or_video ? ' หรือ 1 คลิป' : ''}`,
    num(item.minimum_text_length) > 0 && `ข้อความอย่างน้อย ${num(item.minimum_text_length)} ตัวอักษร`,
    item.deep_review_required && 'ต้องรีวิวเชิงลึก',
    item.onsite_review_required && 'ต้อง Check-in ในรัศมี 100 เมตร',
    item.video_required && 'ต้องมีวิดีโอ',
  ].filter(Boolean) as string[];
  const canManage = item.merchant_can_manage !== false;
  return <article className="mcl-card" aria-label={String(item.name || id)}>
    <header><Link to={`/merchant/campaigns/${encodeURIComponent(id)}`} className="mcl-card__head">
      {banner ? <img src={banner} alt="" /> : <span className="mcl-card__art"><Megaphone /></span>}
      <span><h3>{String(item.name || 'แคมเปญ')}</h3><small>{[place, statusLabel(String(item.admin_approval_status || item.status || '')), `จำนวน ${slots.toLocaleString('th-TH')} รีวิว`].filter(Boolean).join(' · ')}</small></span>
    </Link></header>
    <dl className="mcl-facts">
      <div><dt>Point ทั้งหมด</dt><dd>{pts(item.budget_total)}</dd></div>
      <div><dt>Point ต่อ Review</dt><dd>{pts(item.reward_point || item.point_per_review)}</dd></div>
      <div><dt>วันเริ่ม – สิ้นสุด</dt><dd>{day(item.valid_from) || '—'} – {day(item.valid_to) || 'ไม่กำหนด'}</dd></div>
      <div><dt>การผูกร้าน</dt><dd>{place || '—'}</dd></div>
    </dl>
    {Boolean(conditions.length) && <ul className="mcl-conditions">{conditions.map(text => <li key={text}><CheckCircle2 />{text}</li>)}</ul>}
    <div className="mcl-progresses">
      <Progress label="โควตารีวิว" current={num(item.completed_reviews_count || item.reviews_count)} total={slots} />
      <Progress label="งบประมาณ" current={num(item.budget_used)} total={num(item.budget_total)} />
      <Progress label="จำนวนคน" current={num(item.participants_count || item.rights_claimed)} total={people} />
    </div>
    {canManage && <footer className="mcl-actions">
      <span className={`mcl-status mcl-status--${archived ? 'archived' : status}`}>สถานะ: {archived ? 'Archived' : statusLabel(status)}</span>
      <div>
        <Link className="button button--ghost" to={`/merchant/campaigns/new?campaign_id=${encodeURIComponent(id)}`}><Pencil size={16} />แก้ไข</Link>
        <button type="button" className="button button--ghost" disabled={busy} onClick={() => onAction(item, status === 'active' ? 'pause' : 'resume')}>{status === 'active' ? <><Pause size={16} />หยุด</> : archived ? <><RotateCcw size={16} />กู้คืน</> : <><Play size={16} />เปิด</>}</button>
        {!archived && <button type="button" className="button button--ghost mcl-danger" disabled={busy} onClick={() => onAction(item, 'delete')}><Archive size={16} />Archive</button>}
      </div>
    </footer>}
  </article>;
}

export function MerchantCampaignsPage() {
  const { session } = useAuth();
  const placeId = new URLSearchParams(location.search).get('place_id') || '';
  const [items, setItems] = useState<Row[] | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<Row | null>(null);
  const [notice, setNotice] = useState(() => { const value = sessionStorage.getItem('beaw.merchant.campaign.flash') || ''; sessionStorage.removeItem('beaw.merchant.campaign.flash'); return value; });
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!session?.expert_id) return;
    const controller = new AbortController(); setError('');
    apiRequest<{ items: Row[] }>('/merchant/campaigns', { query: { user_id: session.expert_id, status: 'all', include_archived: true }, signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setItems((data.items || []).filter(isMerchantCampaign)); })
      .catch(reason => { if (!controller.signal.aborted) setError(reason instanceof ApiError ? reason.message : 'โหลดแคมเปญไม่สำเร็จ'); });
    return () => controller.abort();
  }, [session?.expert_id, revision]);

  const scoped = (items || []).filter(item => !placeId || String(item.place_id || item.cafe_id || '').toLowerCase() === placeId.toLowerCase());
  const current = scoped.filter(item => !isArchived(item)), archived = scoped.filter(isArchived);
  const shown = showArchived ? archived : current;
  const placeName = String(scoped.find(item => item.place_name || item.cafe_name)?.place_name || scoped.find(item => item.cafe_name)?.cafe_name || '');

  const act = async (item: Row, action: 'pause' | 'resume' | 'delete') => {
    if (action === 'delete' && confirmArchive !== item) { setConfirmArchive(item); return; }
    setConfirmArchive(null); setBusy(true); setActionError(''); setNotice('');
    try {
      await apiRequest(`/merchant/economy/campaigns/${encodeURIComponent(String(item.campaign_id || item._id))}/${action}`, { method: 'POST', body: { user_id: session?.expert_id } });
      setNotice(ACTION_DONE[action]); if (action === 'delete') setShowArchived(true); else if (action === 'resume') setShowArchived(false);
      setRevision(v => v + 1);
    } catch (reason) { setActionError(`อัปเดตแคมเปญไม่สำเร็จ: ${reason instanceof ApiError ? reason.message : ''}`); }
    finally { setBusy(false); }
  };

  return <WorkspaceLayout kind="merchant" title="แคมเปญ" description="แคมเปญรีวิวของร้าน ใช้งบจาก Merchant Wallet" actions={<Link to={`/merchant/campaigns/new${placeId ? `?place_id=${encodeURIComponent(placeId)}` : ''}`} className="button button--primary"><Plus size={17} />สร้างแคมเปญ</Link>}>
    <div className="mcl">
      {placeId && <p className="mcl-scope">แคมเปญของร้าน <b>{placeName || placeId}</b> <Link to="/merchant/campaigns">ดูทุกร้าน</Link></p>}
      {notice && <p className="success-notice"><CheckCircle2 />{notice}</p>}
      {actionError && <p className="form-error" role="alert">{actionError}</p>}
      <nav className="mcl-tabs" aria-label="สถานะแคมเปญ">
        <button type="button" aria-pressed={!showArchived} onClick={() => setShowArchived(false)}>แคมเปญปัจจุบัน ({current.length})</button>
        <button type="button" aria-pressed={showArchived} onClick={() => setShowArchived(true)}>Archived ({archived.length})</button>
      </nav>
      {error ? <section className="panel"><ErrorState message={error} retry={() => setRevision(v => v + 1)} /></section>
        : !items ? <section className="panel"><Spinner /></section>
        : !shown.length ? <section className="panel mcl-empty"><p>{showArchived ? 'ยังไม่มีแคมเปญที่ Archive' : 'ยังไม่มีแคมเปญปัจจุบัน'}</p>{!showArchived && <Link className="button button--primary" to={`/merchant/campaigns/new${placeId ? `?place_id=${encodeURIComponent(placeId)}` : ''}`}><Plus size={17} />เริ่มแคมเปญรีวิว</Link>}</section>
        : <div className="mcl-list">{shown.map(item => <CampaignCard key={String(item.campaign_id || item._id)} item={item} busy={busy} onAction={(target, action) => void act(target, action)} />)}</div>}
    </div>
    {confirmArchive && <Modal title="เก็บแคมเปญถาวร?" onClose={() => setConfirmArchive(null)}><div className="mcl-modal">
      <p>แคมเปญ “{String(confirmArchive.name || '')}” จะหยุดแสดงและไม่สามารถรับผู้เข้าร่วมใหม่ได้</p>
      <footer><button type="button" className="button button--ghost" onClick={() => setConfirmArchive(null)}>ยกเลิก</button><button type="button" className="button button--danger" disabled={busy} onClick={() => void act(confirmArchive, 'delete')}>Archive</button></footer>
    </div></Modal>}
  </WorkspaceLayout>;
}
